// caiet UC31-05: the admitted list takes IPv4 and IPv6, address or range. The
// Administrator types one string, so the reading of it lives here rather than in
// the screen, and it answers three questions at once: which family, is it well
// formed, and does a range name a network rather than a host.
//
// The third question is not cosmetic. auth.system_credential.allowed_ip_cidrs is
// a Postgres `cidr` column, which refuses a value with bits set to the right of
// the mask. Left to the server, 185.108.12.7/24 comes back as a failure the
// Administrator cannot act on.
//
// These are the server's rules (apps/auth-service/src/client-credential/
// ip-allowlist.ts), so the screen never adds a value the save then refuses: no
// leading zero in an octet (Node's isIP refuses 192.168.01.1) nor in a prefix
// (Postgres' cidr refuses /024). docs/shape-ops-ip-address-forms.md §2, §6.2

export type AddressFormat = 'IPv4' | 'IPv6';

export type AddressVerdict =
  | { valid: true; format: AddressFormat }
  | { valid: false; reason: 'malformed' | 'host_bits' };

const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
const IPV6_GROUP = /^[0-9a-f]{1,4}$/i;
const PREFIX = /^(?:0|[1-9]\d{0,2})$/;

function ipv4Bytes(address: string): number[] | null {
  return IPV4.test(address) ? address.split('.').map(Number) : null;
}

function wordsToBytes(words: string[]): number[] {
  return words.flatMap((word) => {
    const value = Number.parseInt(word, 16);
    return [value >> 8, value & 0xff];
  });
}

function ipv6Bytes(address: string): number[] | null {
  if (!address.includes(':')) return null;

  // A dual-stack socket reports an IPv4 client as ::ffff:127.0.0.1, so that form
  // is one an integrator legitimately admits. Rewriting the trailing quad into
  // two hex words lets the rest of the parse see a uniform list of 16-bit words.
  let normalised = address;
  if (address.includes('.')) {
    const cut = address.lastIndexOf(':') + 1;
    const quad = ipv4Bytes(address.slice(cut));
    if (quad === null) return null;
    const [a, b, c, d] = quad as [number, number, number, number];
    normalised = `${address.slice(0, cut)}${((a << 8) | b).toString(16)}:${((c << 8) | d).toString(16)}`;
  }

  const halves = normalised.split('::');
  if (halves.length > 2) return null;
  const split = (half: string): string[] => (half === '' ? [] : half.split(':'));
  const head = split(halves[0] ?? '');
  const tail = halves.length === 2 ? split(halves[1] ?? '') : [];
  if ([...head, ...tail].some((word) => !IPV6_GROUP.test(word))) return null;

  const zeros = 8 - head.length - tail.length;
  // `::` stands for at least one omitted group; without it the address must be
  // complete on its own.
  if (halves.length === 2 ? zeros < 1 : zeros !== 0) return null;

  return wordsToBytes([
    ...head,
    ...(halves.length === 2 ? Array<string>(zeros).fill('0') : []),
    ...tail,
  ]);
}

function hostBitsClear(bytes: number[], prefixLength: number): boolean {
  return bytes.every((byte, index) => {
    const covered = Math.min(Math.max(prefixLength - index * 8, 0), 8);
    return (byte & (0xff >> covered)) === 0;
  });
}

interface ReadValue {
  format: AddressFormat;
  bytes: number[];
  prefix: number | null;
}

function readValue(value: string): ReadValue | 'malformed' | 'host_bits' {
  const slash = value.indexOf('/');
  const address = slash === -1 ? value : value.slice(0, slash);
  const prefixText = slash === -1 ? null : value.slice(slash + 1);

  const quad = ipv4Bytes(address);
  const bytes = quad ?? ipv6Bytes(address);
  if (bytes === null) return 'malformed';
  const format: AddressFormat = quad === null ? 'IPv6' : 'IPv4';

  if (prefixText === null) return { format, bytes, prefix: null };
  const widest = format === 'IPv4' ? 32 : 128;
  if (!PREFIX.test(prefixText) || Number(prefixText) > widest) return 'malformed';
  const prefix = Number(prefixText);
  if (!hostBitsClear(bytes, prefix)) return 'host_bits';
  return { format, bytes, prefix };
}

export function inspectAddress(value: string): AddressVerdict {
  const read = readValue(value);
  return typeof read === 'string'
    ? { valid: false, reason: read }
    : { valid: true, format: read.format };
}

// The addresses a value admits, in the IPv4-mapped space the server compares in
// (::ffff:a.b.c.d): its network bytes and prefix, one address being its own /128.
function admittedSet(value: string): string | null {
  const read = readValue(value);
  if (typeof read === 'string') return null;
  const ipv4 = read.format === 'IPv4';
  const bytes = ipv4 ? [...Array<number>(10).fill(0), 0xff, 0xff, ...read.bytes] : read.bytes;
  const prefix = read.prefix === null ? 128 : read.prefix + (ipv4 ? 96 : 0);
  return `${bytes.join('.')}/${prefix}`;
}

// Whether two typed values admit the same addresses, as the server reads them:
// 2001:DB8::10, 2001:0db8::10 and 2001:db8::10/128 are one entry, and so are
// 10.0.0.10 and ::ffff:10.0.0.10. The list's duplicate check, so a second
// spelling is named at once instead of becoming a row that admits nothing new.
export function sameEntry(a: string, b: string): boolean {
  const left = admittedSet(a);
  return left !== null && left === admittedSet(b);
}

export function addressFormat(value: string): AddressFormat | null {
  const verdict = inspectAddress(value);
  return verdict.valid ? verdict.format : null;
}
